import React, { useState, useEffect } from 'react';
import { Star } from 'lucide-react';
import { socialApi } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const StarButton = ({ skillId, initialStarred = false, showCount = true, starCount = 0, size = 'default', className = '' }) => {
  const { user } = useAuth();
  const [isStarred, setIsStarred] = useState(initialStarred);
  const [count, setCount] = useState(starCount);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && skillId) {
      checkStarStatus();
    }
  }, [user, skillId]);

  const checkStarStatus = async () => {
    try {
      const res = await socialApi.isStarred(skillId);
      setIsStarred(res.data.is_starred);
    } catch (error) {
      console.error('Failed to check star status', error);
    }
  };

  const handleToggleStar = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      toast.error('Please sign in to star skills');
      return;
    }

    setLoading(true);
    try {
      if (isStarred) {
        await socialApi.unstar(skillId);
        setIsStarred(false);
        setCount(prev => Math.max(0, prev - 1));
        toast.success('Removed from starred');
      } else {
        await socialApi.star(skillId);
        setIsStarred(true);
        setCount(prev => prev + 1);
        toast.success('Added to starred');
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        toast.error('Please sign in to star skills');
      } else {
        toast.error('Failed to update star');
      }
    } finally {
      setLoading(false);
    }
  };

  const sizeClasses = {
    small: 'h-4 w-4',
    default: 'h-5 w-5',
    large: 'h-6 w-6',
  };

  return (
    <button
      onClick={handleToggleStar}
      disabled={loading}
      className={`
        flex items-center gap-1.5 px-3 py-1.5 rounded-lg
        transition-all duration-200
        ${isStarred 
          ? 'bg-primary/10 text-primary hover:bg-primary/20' 
          : 'bg-gray-100 dark:bg-dark-card text-text-gray dark:text-dark-text-secondary hover:bg-gray-200 dark:hover:bg-dark-border'
        }
        ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
      title={isStarred ? 'Remove from starred' : 'Add to starred'}
    >
      <Star 
        className={`${sizeClasses[size]} transition-all ${isStarred ? 'fill-primary' : ''}`}
      />
      {showCount && (
        <span className="text-sm font-medium">{count}</span>
      )}
    </button>
  );
};

export default StarButton;
